import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType, Effect, createEffect } from '@ngrx/effects';
import { switchMap, map, catchError, tap, concatMap } from 'rxjs/operators';
import { of, merge } from 'rxjs';

import { AuthService } from '../auth.service';
import { UtentiService } from '../../utenti/utenti.service';
import * as authActions from './auth.actions';
import { Utente } from 'src/app/shared/models/utente.model';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { NoteType } from 'src/app/shared/models/note-type.model';
import { AttachmentType } from 'src/app/shared/models/attachment-type.model';

/**Classe che gestisce gli effetti per la sezione di autenticazione */
@Injectable()
export class AuthEffects {
    /**
     * Costruttore per AuthEffects
     * @param actions$ {Actions} azioni definite per ngrx-effects
     * @param authService {AuthService} servizio che gestisce le chiamate di autenticazione
     * @param router {Router} per la navigazione
     * @param utility {UtilityService} servizio contenente utilità comuni varie
     * @param utentiService {UtentiService} classe dei servizi relativi alla sezione Utenti
     */
    constructor(
        private actions$: Actions,
        private authService: AuthService,
        private router: Router,
        private utility: UtilityService,
        private utentiService: UtentiService,
    ) { }

    /**Side effect per l'azione di Login, chiama il servizio per l'autenticazione e gestisce la risposta direzionandola fra LoginSuccess e LoginFailure */
    @Effect()
    public login$ = this.actions$.pipe(
        ofType(authActions.LOGIN),
        switchMap((action: authActions.Login) =>
            this.authService.login(action.payload.username, action.payload.password).pipe(
                map(resData => {
                    const utente = <Utente>{
                        id: resData.id,
                        type: resData.type,
                        username: resData.username,
                        name: resData.name,
                        email: resData.email,
                        enabled: resData.enabled,
                        key: resData.key,
                        password: "",
                        settings: resData.settings
                    };
                    // const utente = {...resData};
                    localStorage.setItem('loggedUser', JSON.stringify(utente));
                    this.authService.setLogoutTimer();
                    this.router.navigate(['/'], {skipLocationChange: true});
                    return new authActions.LoginSuccess(utente);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString("auth." + errRes.error));
                    }
                    return of(new authActions.LoginFailure(errRes));
                })
            )
        )
    )

    /**Side effect per l'azione di AutoLogin, recupera l'utente dal localStorage se presente */
    @Effect()
    autoLogin = this.actions$.pipe(
        ofType(authActions.AUTO_LOGIN),
        map(() => {
            let tempUser: Utente;
            let currentDate = new Date().valueOf();
            let expirationDate = Number(localStorage.getItem('expirationDate'));
            if (localStorage.getItem('loggedUser') && (currentDate < expirationDate)) {
                let localUser = JSON.parse(localStorage.getItem('loggedUser'));
                tempUser = { ...localUser };
                this.authService.setLogoutTimer();
                return new authActions.LoginSuccess(tempUser);
            } else {
                return { type: 'dummy' };
            }
        })
    )

    /**Side effect per l'azione LoginSuccess, effettua la navigazione sulla pagina Home dell'applicazione */
    @Effect()
    loginSuccess = this.actions$.pipe(
        ofType(authActions.LOGIN_SUCCESS),
        switchMap((action: authActions.LoginSuccess) => {
            return [
                new authActions.StartGetRoles(),
                new authActions.StartGetResType(),
                new authActions.StartGetGlosType(),
                new authActions.StartGetNoteType(),
                new authActions.StartGetAttachmentTypes(),
                // new authActions.StartGetAssignedResources(action.payload.id),
                // new authActions.StartGetTemporary(action.payload.id)
            ];
        })
    );

    /**Side effect del logout, procede a svuotare il localStorage e naviga nuovamente sulla pagina di login */
    @Effect({ dispatch: false })
    authLogout = this.actions$.pipe(
        ofType(authActions.LOGOUT),
        tap(() => {
            localStorage.removeItem('loggedUser');
            this.authService.clearLogoutTimer();
            this.router.navigate(['/auth'], {skipLocationChange: true});
        })
    );

    /**Side effect dell'azione di avvio keepalive, evoca il servizio che richiede al BE il rinnovo della key di identificazione univoca */
    @Effect()
    keepaliveStart = this.actions$.pipe(
        ofType(authActions.START_KEEPALIVE),
        switchMap(() =>
            this.authService.keepaliveKey().pipe(
                map(() => {
                    return new authActions.KeepaliveSuccess();
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('auth.' + errRes.error));
                    }
                    return of(new authActions.KeepaliveFailure());
                })
            )
        )
    )

    /**Side effect che recepisce l'avvenuto rinnovo della key di identificazione univoca */
    @Effect()
    keepaliveSuccess = this.actions$.pipe(
        ofType(authActions.KEEPALIVE_SUCCESS),
        switchMap(() => {
            this.authService.clearLogoutTimer(); //azzero il timer di logout
            this.authService.setLogoutTimer(); //inserisco il nuovo timer di logout
            return of(new authActions.ShowWarning(false));
        })
    );

    /**Side effect per l'inizio di modifica dei campi dell'utente loggato*/

    startUpdateLoggedUser = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.START_UPDATE_LOGGEDUSER),
            concatMap((action: authActions.StartUpdateLoggedUser) => {
                return merge(
                    ...action.payload.map(modifica =>
                        this.utentiService.updateUserV2(modifica.user, modifica.nomeCampo, modifica.newValue).pipe(
                            map(resData =>
                                new authActions.UpdateLoggedUserSuccess(resData)),
                            catchError(errRes => {
                                if (errRes.status === 500) {
                                    this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                                } else if (errRes.error) {
                                    this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('updateUser.' + errRes.error));
                                }
                                return of(new authActions.UpdateLoggedUserFailure('errore'));
                            }
                            )
                        )
                    ))
            }))
    );

    /** Side effect per l'avvenuta modifica dello loggedUser */
    @Effect({ dispatch: false })
    UpdateLoggedUserSuccess = this.actions$.pipe(
        ofType(authActions.UPDATE_LOGGEDUSER_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
            this.utility.gestioneSnackbar(this.utility.getTranslatedString("utentiComponentString.updatedUser"));
        })
    );


    /**Side effect per l'azione di inizio recupero tipologie di ruolo, richiama il servizio che gestisce la chiamata a BE */
    @Effect()
    startGetRoles = this.actions$.pipe(
        ofType(authActions.START_GET_ROLES),
        switchMap(() =>
            this.authService.getTipiProfilo().pipe(
                map(resData => {
                    return new authActions.GetRolesSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getRolesType' + errRes.error));
                    }
                    return of(new authActions.GetRolesFailure(errRes));
                })
            )
        )
    );


    /**Side effect per l'avvenuto recupero dell'elenco ruoli */
    @Effect({ dispatch: false })
    getRolesSuccess = this.actions$.pipe(
        ofType(authActions.GET_ROLES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero elenco risorse, richiama il servizio che gestisce il recupero da BE */
    @Effect()
    startGetResourceType = this.actions$.pipe(
        ofType(authActions.START_GET_RES_TYPE),
        switchMap(() =>
            this.authService.getTipiRisorsa().pipe(
                map(resData => {
                    return new authActions.GetResTypeSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getResourceType' + errRes.error));
                    }
                    return of(new authActions.GetResTypeFailure(errRes));
                })
            )
        )
    );

    /**Side effect che recepisce la risposta del BE in caso di effettivo recupero delle tipologie di risorsa */
    @Effect({ dispatch: false })
    getResTypeSuccess = this.actions$.pipe(
        ofType(authActions.GET_RES_TYPE_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );

    /**Side effect per l'azione di inizio recupero dei tipi di glossario disponibili */
    @Effect()
    startGetGlosType = this.actions$.pipe(
        ofType(authActions.START_GET_GLOS_TYPE),
        switchMap(() =>
            this.authService.getTipiGlossario().pipe(
                map(resData => {
                    return new authActions.GetGlosTypeSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getGlossaryType' + errRes.error));
                    }
                    return of(new authActions.GetGlosTypeFailure(errRes));
                })
            )
        )
    );

    /**Side effect che recepisce la risposta del BE in caso di effettivo recupero delle tipologie di glossario */
    @Effect({ dispatch: false })
    getGlosTypeSuccess = this.actions$.pipe(
        ofType(authActions.GET_GLOS_TYPE_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );

    /**Side effect per l'azione di inizio recupero dei tipi di nota disponibili */
    @Effect()
    startGetNoteType = this.actions$.pipe(
        ofType(authActions.START_GET_NOTE_TYPE),
        switchMap(() =>
            this.authService.getTipiNota().pipe(
                map((resData: NoteType[]) => new authActions.GetNoteTypeSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getNoteType' + errRes.error));
                    }
                    return of(new authActions.GetNoteTypeFailure());
                })
            )
        )
    );

    /**Side effect che recepisce la risposta del BE in caso di effettivo recupero delle tipologie di nota */
    @Effect({ dispatch: false })
    getNoteTypeSuccess = this.actions$.pipe(
        ofType(authActions.GET_NOTE_TYPE_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );

    /**Side effect per l'azione di inizio recupero dell'elenco delle tipologie di allegato */
    @Effect()
    startGetAttachmentType = this.actions$.pipe(
        ofType(authActions.START_GET_ATTACHMENT_TYPES),
        switchMap(() => 
            this.authService.getTipiAllegati().pipe(
                map((resData: AttachmentType[]) => new authActions.GetAttachmentTypesSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getAttachmentType' + errRes.error));
                    }
                    return of(new authActions.GetAttachmentTypesFailure());
                })
            )
        )
    );

    /**Side effect che recepisce la risposta del BE in caso di effettivo recupero delle tipologie di allegato */
    @Effect({ dispatch: false })
    getAttachmentTypeSuccess = this.actions$.pipe(
        ofType(authActions.GET_ATTACHMENT_TYPES_SUCCESS),
        tap(() => this.authService.refreshLogoutTimer())
    );    

    /**Side effect per l'azione di inizio recupero elenco risorse assegnate, richiama il servizio che gestisce la chiamata a BE */
    @Effect()
    startGetAssignedResources = this.actions$.pipe(
        ofType(authActions.START_GET_ASSIGNED_RESOURCES),
        switchMap((action: authActions.StartGetAssignedResources) =>
            this.utentiService.getUserResources(action.payload).pipe(
                map(resData => {
                    return new authActions.GetAssignedResourcesSuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getAccountResources' + errRes.error));
                    }
                    return of(new authActions.GetAssignedResourcesFailure());
                })
            )
        )
    );


    /**Side effect per l'avvenuto recupero dell'elenco risorse assegnate */
    @Effect({ dispatch: false })
    getAssignedResources = this.actions$.pipe(
        ofType(authActions.GET_ASSIGNED_RESOURCES_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio recupero delle note personali */
    @Effect()
    startGetTemporary = this.actions$.pipe(
        ofType(authActions.START_GET_TEMPORARY),
        switchMap((action: authActions.StartGetTemporary) =>
            this.authService.getTemporary(action.payload).pipe(
                map(resData => {
                    return new authActions.GetTemporarySuccess(resData);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('getTemporary.' + errRes.error));
                    }
                    return of(new authActions.GetTemporaryFailure())
                })
            )
        )
    );

    /**Side effect per l'avvenuto recupero delle note personali */
    @Effect({ dispatch: false })
    getTemporarySuccess = this.actions$.pipe(
        ofType(authActions.GET_TEMPORARY_SUCCES),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    /**Side effect per l'azione di inizio aggiornamento delle note personali */
    @Effect()
    startPostTemporary = this.actions$.pipe(
        ofType(authActions.START_POST_TEMPORARY),
        switchMap((action: authActions.StartPostTemporary) =>
            this.authService.postTemporary(action.payload.idUtente, action.payload.newNote).pipe(
                map(() => {
                    return new authActions.PostTemporarySuccess(action.payload.newNote);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('postTemporary.' + errRes.error));
                    }
                    return of(new authActions.PostTemporaryFailure())
                })
            )
        )
    );

    /**Side effect per l'avvenuto aggiornamento delle note personali */
    @Effect({ dispatch: false })
    postTemporarySuccess = this.actions$.pipe(
        ofType(authActions.POST_TEMPORARY_SUCCES),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    
}